import { useState } from 'react';
import { Mic, Loader2, Check } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { TenantCampaignWithCapabilitiesDto } from '@signalvote/shared';
import { useCampaignStore } from '@/stores/campaignStore';
import { useUpdateTenantCampaign } from '@/hooks/queries/useTenantCampaigns';

export function VoiceTranscriptionSettingsCard() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const canManageTeam = useCampaignStore((s) => s.canManage('TeamManagement'));

  if (!selectedCampaign || !canManageTeam) return null;

  // Keyed on the campaign: switching campaigns starts fresh, while a refetch of
  // the SAME campaign leaves an in-progress edit alone.
  return <VoiceSettingsForm key={selectedCampaign.id} selectedCampaign={selectedCampaign} />;
}

function VoiceSettingsForm({
  selectedCampaign,
}: {
  selectedCampaign: TenantCampaignWithCapabilitiesDto;
}) {
  const [enabled, setEnabled] = useState(selectedCampaign.voiceTranscriptionEnabled);
  const [retentionDays, setRetentionDays] = useState(selectedCampaign.voiceRetentionDays);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { mutate: updateCampaign, isPending, failure } = useUpdateTenantCampaign();

  const hasChanges =
    enabled !== selectedCampaign.voiceTranscriptionEnabled ||
    retentionDays !== selectedCampaign.voiceRetentionDays;

  const handleSave = () => {
    setSaveSuccess(false);
    updateCampaign(
      {
        id: selectedCampaign.id,
        data: {
          voiceTranscriptionEnabled: enabled,
          voiceRetentionDays: retentionDays,
        },
      },
      {
        onSuccess: () => {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mic className="h-5 w-5" />
          Voice Transcription (Beta)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="voice-toggle" className="text-sm font-medium">
              Enable Voice Transcription
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Allow canvassers to record and transcribe doorstep conversations.
            </p>
          </div>
          <Switch
            id="voice-toggle"
            checked={enabled}
            onCheckedChange={(checked) => {
              setEnabled(checked);
              setSaveSuccess(false);
            }}
          />
        </div>

        <div>
          <Label htmlFor="retention-days" className="text-sm font-medium">
            Recording Retention (days)
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">
            Audio recordings are automatically deleted after this many days.
          </p>
          <Input
            id="retention-days"
            type="number"
            min={1}
            value={retentionDays}
            onChange={(e) => {
              setRetentionDays(Math.max(1, parseInt(e.target.value, 10) || 1));
              setSaveSuccess(false);
            }}
            className="w-32"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleSave} disabled={!hasChanges || isPending}>
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
          {saveSuccess && (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <Check className="h-4 w-4" />
              Saved
            </span>
          )}
          {failure && (
            <span className="text-sm text-destructive">Failed to save. Please try again.</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
